import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLens } from '@hookform/lenses';

import { AnyChatMediaUpload, AnyChatForm } from '@anyx/feature-crm/shared';


import { useBroadcastImageUpload } from '../hooks';
import { LineMessageFormSubView, useLineMessageStore } from '../store';
import { LineMessageFormFieldsProps, RichMessageBlockFieldErrors } from '../types';
import { UrlActionBlock } from '../url-action-block';

export function RichMessageBlock({ view: { blockIndex } }: { view: LineMessageFormSubView }) {
  const { t } = useTranslation('crm');
  const { register, control, unregister, setValue, formState } =
    useFormContext<LineMessageFormFieldsProps>();

  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const trackingEnabled = useWatch({
    control,
    name: 'trackingEnabled',
  });

  const imageFiles = useWatch({
    control,
    name: `messages.${blockIndex}.image` as const,
  });

  const actionUrlStatus = useWatch({
    control,
    name: `messages.${blockIndex}.actionUrl.status` as const,
  });

  const lens = useLens({
    control,
  });

  const mediaUploadProps = useBroadcastImageUpload({
    messageType: 'RICH',
    onImageUpdate: (image) => {
      setValue(`messages.${blockIndex}.image` as const, image, {
        shouldValidate: formState.submitCount > 0,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  });

  const error = useMemo(() => {
    const errorMsg = formState.errors?.messages?.[blockIndex] as RichMessageBlockFieldErrors;

    if (errorMsg && ('image' in errorMsg || 'actionUrl' in errorMsg)) {
      return errorMsg;
    }

    return undefined;
  }, [blockIndex, formState]);

  useEffect(() => {
    register(`messages.${blockIndex}.image`);

    return () => {
      unregister(
        [`messages.${blockIndex}.image` as const, `messages.${blockIndex}.actionUrl` as const],
        {
          keepDefaultValue: true,
          keepDirty: true,
          keepError: true,
          keepIsValid: true,
          keepTouched: true,
          keepValue: true,
        }
      );
    };
  }, [register, unregister, blockIndex]);

  return (
    <AnyChatForm.Block>
      <AnyChatMediaUpload
        {...mediaUploadProps}
        inputWrapperProps={{
          label: t('crm.lineMessageForm.fields.image.label'),
          error: error?.image?.message,
          required: true,
        }}
        disabled={isFormDisabled}
        files={imageFiles ? [imageFiles] : []}
      />
      <UrlActionBlock
        lens={lens.reflect((_, l) => ({
          status: l.focus(`messages.${blockIndex}.actionUrl.status` as const),
          manual: l.focus(`messages.${blockIndex}.actionUrl.manual` as const),
          variable: l.focus(`messages.${blockIndex}.actionUrl.variable` as const),
        }))}
        trackingEnabled={trackingEnabled}
        actionStatus={actionUrlStatus}
      />
    </AnyChatForm.Block>
  );
}
